// ApiContext.js - Reemplaza FirebaseContext usando el nuevo backend
import {
	createContext,
	useContext,
	useEffect,
	useCallback,
	useMemo,
	useState,
} from "react";
import api from "../utils/api";
import * as analytics from "../utils/analytics";
import { logger } from "../hooks/Logger";
import { safeStorage } from "../utils/safeStorage";
// Travel Kit: plantilla reutilizable para cada viaje (solo esta es local; el resto vienen del backend)
import travelKitData from "../data/prebuiltGuides/travelkit";

const ApiContext = createContext();

export const ApiProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [subscription, setSubscription] = useState(null);
	const [limits, setLimits] = useState(null);
	const [purchasedGuides, setPurchasedGuides] = useState([]);
	// Entitlements del usuario (packs comprados, stickers, páginas extra…).
	// Objeto serializado tal cual lo devuelve /api/packs/me/entitlements.
	// Forma: { prebuiltGuides, customStickerCountries, extraPagesPerGuide,
	//          ownedPackIds, ownedGuideIds, isPro, proUnlocks }
	const [entitlements, setEntitlements] = useState(null);
	const [favorites, setFavorites] = useState([]);
	const [initialLoading, setInitialLoading] = useState(true);
	const [emailVerified, setEmailVerified] = useState(false);
	const [dismissedNotificationIds, setDismissedNotificationIds] = useState(
		() => {
			try {
				const saved = safeStorage.getItem("guia_dismissed_notifications");
				return saved ? JSON.parse(saved) : [];
			} catch {
				return [];
			}
		},
	);
	const [eventNotifications, setEventNotifications] = useState([]);

	/**
	 * Añade una notificación por evento (crear guía, etc.)
	 * @param {Object} notif - { id, type, title, message, action? }
	 */
	const addNotification = (notif) => {
		const id = notif.id || `event-${Date.now()}`;
		setEventNotifications((prev) => [
			...prev,
			{
				id,
				type: notif.type || "success",
				title: notif.title,
				message: notif.message,
				action: notif.action,
				createdAt: new Date().toISOString(),
				isEvent: true,
			},
		]);
	};

	// Notificaciones del sistema (derivadas de limits, subscription)
	const systemNotifications = useMemo(() => {
		if (!user) return [];
		const list = [];
		const now = new Date();

		// Límite de guías alcanzado (plan gratuito)
		if (limits && !limits.isComplete && limits.maxGuides > 0) {
			const { currentGuides, maxGuides } = limits;
			if (currentGuides >= maxGuides) {
				list.push({
					id: "guide-limit-reached",
					type: "warning",
					title: "Límite de guías alcanzado",
					message: `Tu plan gratuito incluye ${maxGuides} guía${maxGuides !== 1 ? "s" : ""}. Hazte Pro para crear guías ilimitadas.`,
					action: { label: "Ver planes", path: "/descuentos" },
					createdAt: now.toISOString(),
				});
			} else if (currentGuides === maxGuides - 1) {
				list.push({
					id: "guide-limit-warning",
					type: "info",
					title: "Última guía disponible",
					message: `Te queda 1 guía en el plan gratuito. Explora plantillas Pro.`,
					action: { label: "Explorar", path: "/explorar" },
					createdAt: now.toISOString(),
				});
			}
		}

		// Suscripción: renovación próxima
		if (subscription?.isActive && subscription?.currentPeriodEnd) {
			const endDate = new Date(subscription.currentPeriodEnd);
			const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
			if (daysLeft <= 7 && daysLeft > 0) {
				list.push({
					id: "subscription-renewal-soon",
					type: "info",
					title: "Renovación próxima",
					message: `Tu suscripción Pro se renueva en ${daysLeft} día(s).`,
					action: { label: "Gestionar", handler: "portal" },
					createdAt: now.toISOString(),
				});
			}
		}

		// Suscripción cancelada (renueva hasta fin de periodo)
		if (subscription?.cancelAtPeriodEnd) {
			list.push({
				id: "subscription-cancelled",
				type: "info",
				title: "Suscripción cancelada",
				message: "Tu acceso Pro continúa hasta el final del periodo actual.",
				action: { label: "Reactivar", handler: "portal" },
				createdAt: now.toISOString(),
			});
		}

		// Filtrar las que el usuario ha ocultado
		return list.filter((n) => !dismissedNotificationIds.includes(n.id));
	}, [user, limits, subscription, dismissedNotificationIds]);

	// Notificaciones combinadas: eventos + sistema
	const notifications = useMemo(
		() => [...eventNotifications, ...systemNotifications],
		[eventNotifications, systemNotifications],
	);

	const unreadNotificationCount = notifications.length;

	const dismissNotification = (id) => {
		// Event notifications: eliminar de la lista
		if (eventNotifications.some((n) => n.id === id)) {
			setEventNotifications((prev) => prev.filter((n) => n.id !== id));
			return;
		}
		// System notifications: marcar como ocultas
		setDismissedNotificationIds((prev) => {
			const next = [...prev, id];
			safeStorage.setItem(
				"guia_dismissed_notifications",
				JSON.stringify(next),
			);
			return next;
		});
	};

	const dismissAllNotifications = () => {
		setEventNotifications([]);
		const ids = systemNotifications.map((n) => n.id);
		setDismissedNotificationIds((prev) => {
			const next = [...new Set([...prev, ...ids])];
			safeStorage.setItem(
				"guia_dismissed_notifications",
				JSON.stringify(next),
			);
			return next;
		});
	};

	// Verificar si hay un token guardado y obtener el usuario
	useEffect(() => {
		let cancelled = false;
		let finished = false;
		const finishLoading = () => {
			if (finished || cancelled) return;
			finished = true;
			setInitialLoading(false);
		};
		const bootCap = setTimeout(finishLoading, 2500);

		let token = null;
		try {
			token = safeStorage.getItem("token");
		} catch (_) {
			finishLoading();
			return () => {
				cancelled = true;
				clearTimeout(bootCap);
			};
		}

		if (token) {
			api.setToken(token);
			// Obtener información del usuario (ahora incluye suscripción, límites y compras)
			api
				.get("/auth/me")
				.then((response) => {
					setUser({
						uid: response.user.id,
						email: response.user.email,
						displayName: response.user.displayName,
						...response.user,
					});
					analytics.setUserId(response.user.id);
					setEmailVerified(response.user.emailVerified ?? false);
					if (response.subscription) {
						setSubscription(response.subscription);
					}
					if (response.limits) {
						setLimits(response.limits);
					}
					if (response.purchasedGuides) {
						setPurchasedGuides(response.purchasedGuides);
					}
					if (response.favorites) {
						setFavorites(response.favorites);
					}
					// Entitlements (packs comprados, stickers, páginas extra…)
					api
						.get("/packs/me/entitlements")
						.then((res) => {
							if (res?.entitlements) setEntitlements(res.entitlements);
						})
						.catch(() => {
							// no es bloqueante; la UI puede operar sin entitlements
						});
					finishLoading();
				})
				.catch((error) => {
					logger.error("ApiContext: error al verificar usuario", {
						message: error?.message,
					});
					safeStorage.removeItem("token");
					api.setToken(null);
					setUser(null);
					setSubscription(null);
					setLimits(null);
					setPurchasedGuides([]);
					setEntitlements(null);
					setFavorites([]);
					finishLoading();
				});
		} else {
			finishLoading();
		}
		return () => {
			cancelled = true;
			clearTimeout(bootCap);
		};
	}, []);

	const removeUndefinedFields = (obj) => {
		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				if (
					typeof obj[key] === "object" &&
					obj[key] !== null &&
					!Array.isArray(obj[key])
				) {
					removeUndefinedFields(obj[key]);
				} else if (obj[key] === undefined) {
					delete obj[key];
				}
			}
		}
		if (Array.isArray(obj)) {
			obj = obj.filter((item) => typeof item !== "undefined");
		}
		return obj;
	};

	const updateProfile = async (data) => {
		try {
			const response = await api.patch("/user/profile", data);
			if (response?.user) {
				setUser((prev) => (prev ? { ...prev, ...response.user } : null));
				return response.user;
			}
			return null;
		} catch (e) {
			logger.error("ApiContext: error actualizando perfil", {
				message: e?.message,
			});
			throw e;
		}
	};

	const handleLogout = async () => {
		try {
			analytics.trackEvent("logout");
			analytics.clearUserId();
			safeStorage.removeItem("token");
			api.setToken(null);
			setUser(null);
			setSubscription(null);
			setLimits(null);
			setPurchasedGuides([]);
			setEntitlements(null);
			setFavorites([]);
			setEmailVerified(false);
			window.location.reload();
		} catch (error) {
			logger.error("ApiContext: error durante logout", {
				message: error?.message,
			});
		}
	};

	const createUser = async () => {
		// Ya se crea automáticamente al registrarse
		console.log("Usuario creado automáticamente");
	};

	const createProject = async (name = "Mi nuevo proyecto") => {
		try {
			const project = await api.post("/projects", {
				name: name,
				backgroundColor: "#FFFFFF",
				textColor: "#000000",
				headerBackgroundColor: "rgb(47, 127, 224)",
				headerTextColor: "#FFFFFF",
				fontFamily: "Montserrat",
				public: false,
			});
			analytics.trackEvent("guide_created", {
				guide_id: String(project.id),
				guide_name: name,
			});
			// Notificación de guía creada
			addNotification({
				id: `event-guide-created-${project.id}`,
				type: "success",
				title: "¡Guía creada!",
				message: `"${name}" está lista para editar. Añade páginas y personaliza tu guía.`,
				action: {
					label: "Abrir guía",
					handler: "openGuide",
					projectId: project.id,
				},
			});
			// Simular docRef para compatibilidad; incluir userId para navegación
			return {
				id: project.id,
				docRef: { id: project.id },
				userId: project.userId,
			};
		} catch (e) {
			console.error("Error adding document: ", e);
			return null;
		}
	};

	const readProjects = async () => {
		try {
			const projects = await api.get("/projects");
			return projects.map((project) => ({
				id: project.id,
				docRef: { id: project.id },
				...project,
			}));
		} catch (e) {
			console.error("Error reading projects: ", e);
			return [];
		}
	};

	/**
	 * Lee proyectos del usuario paginados (para scroll infinito).
	 * @param {{ page: number, limit?: number }} opts - page (1-based), limit opcional (default 12)
	 * @returns {{ projects: Array, total: number, hasMore: boolean }}
	 */
	const readProjectsPaginated = async (opts = {}) => {
		try {
			const page = Math.max(1, opts.page || 1);
			const limit = Math.min(50, Math.max(1, opts.limit || 12));
			const data = await api.get(`/projects?page=${page}&limit=${limit}`);
			const projects = (data.projects || []).map((project) => ({
				id: project.id,
				docRef: { id: project.id },
				...project,
			}));
			return {
				projects,
				total: data.total ?? projects.length,
				hasMore: Boolean(data.hasMore),
			};
		} catch (e) {
			console.error("Error reading projects (paginated): ", e);
			return { projects: [], total: 0, hasMore: false };
		}
	};

	const readPublicProjects = async () => {
		try {
			const projects = await api.get("/projects/public");
			return projects.map((project) => ({
				id: project.id,
				docRef: { id: project.id },
				...project,
			}));
		} catch (e) {
			console.error("Error reading public projects: ", e);
			return [];
		}
	};

	const readProject = async (userId, projectId) => {
		try {
			const project = await api.get(`/projects/${userId}/${projectId}`);
			return {
				id: project.id,
				docRef: { id: project.id },
				...project,
			};
		} catch (e) {
			console.error("Error reading project: ", e);
			return null;
		}
	};

	const updateProject = async (
		id,
		name,
		backgroundColor,
		textColor,
		headerBackgroundColor,
		headerTextColor,
		fontFamily,
		isPublic = false,
		coverImage,
	) => {
		try {
			const body = {
				name,
				backgroundColor,
				textColor,
				headerBackgroundColor,
				headerTextColor,
				fontFamily,
				public: isPublic,
			};
			if (coverImage !== undefined) {
				body.coverImage = coverImage;
			}
			await api.put(`/projects/${id}`, body);
			console.log("Document update with ID: ", id);
		} catch (e) {
			console.error("Error updating document: ", e);
		}
	};

	const deleteProject = async (id) => {
		try {
			await api.delete(`/projects/${id}`);
			console.log("Document deleted with ID: ", id);
			// Actualizar límites en el estado para que canCreateGuide refleje el nuevo conteo
			try {
				const response = await api.get("/auth/me");
				if (response.limits) {
					setLimits(response.limits);
				}
			} catch (e) {
				console.warn("Error refreshing limits after delete:", e);
			}
		} catch (e) {
			console.error("Error deleting document: ", e);
		}
	};

	const createPage = async (projectId, values) => {
		try {
			// Empaquetar los datos en el campo data para el backend
			const {
				id,
				docRef,
				projectId: _,
				updatedAt: __,
				order,
				...pageData
			} = values;
			const page = await api.post(`/pages/${projectId}`, {
				...pageData,
				// Incluir el order si existe
				order: order !== undefined ? order : 0,
				// Si ya viene con data, usarlo; si no, empaquetar las propiedades principales
				data: values.data || {
					templateId: values.templateId,
					images: values.images,
					texts: values.texts,
					maps: values.maps,
					transport: values.transport,
					notes: values.notes || [],
					canvasStickers: values.canvasStickers,
					canvasImages: values.canvasImages,
					canvasTexts: values.canvasTexts,
				},
			});
			return {
				id: page.id,
				docRef: { id: page.id },
			};
		} catch (e) {
			console.error("Error adding document: ", e);
			return null;
		}
	};

	const readPages = async (projectId) => {
		try {
			const response = await api.get(`/pages/${projectId}`);
			// Aceptar tanto array directo como respuesta envuelta (ej. { data: [...] })
			const rawPages = Array.isArray(response)
				? response
				: (response?.data ?? response?.pages ?? []);
			const pagesArray = Array.isArray(rawPages) ? rawPages : [];
			const sortedPages = [...pagesArray].sort((a, b) => {
				const orderA = a.order ?? 0;
				const orderB = b.order ?? 0;
				return orderA - orderB;
			});
			return sortedPages.map((page, index) => {
				// Extraer el contenido de data y colocarlo directamente en la página
				const pageData = page.data || {};
				// Usar el order de la página si existe, sino usar el índice (que ya está ordenado)
				const pageOrder =
					page.order !== undefined && page.order !== null ? page.order : index;
				const templateId =
					pageData.templateId !== undefined && pageData.templateId !== null
						? Number(pageData.templateId)
						: 0;
				return {
					id: page.id,
					docRef: { id: page.id },
					order: pageOrder,
					projectId: page.projectId,
					updatedAt: page.updatedAt,
					// Extraer propiedades de data (templateId como número para que coincida con templates 101-106)
					templateId,
					images: pageData.images || [],
					texts: pageData.texts || [],
					maps: pageData.maps || ["", "", ""],
					transport: pageData.transport || [],
					notes: pageData.notes || [],
					canvasStickers: pageData.canvasStickers || [],
					canvasImages: pageData.canvasImages || [],
					canvasTexts: pageData.canvasTexts || [],
					// Preservar el objeto data completo para templates especiales (como Template24)
					data: pageData,
				};
			});
		} catch (e) {
			console.error("Error reading pages: ", e);
			return [];
		}
	};

	const updatePage = async (projectId, id, values) => {
		try {
			// Empaquetar los datos en el campo data para el backend
			const {
				id: _,
				docRef: __,
				projectId: ___,
				updatedAt: ____,
				order: _____,
				...pageData
			} = values;
			const dataToSend = values.data || {
				templateId: values.templateId,
				images: values.images,
				texts: values.texts,
				maps: values.maps,
				transport: values.transport,
				notes: values.notes || [],
				canvasStickers: values.canvasStickers,
				canvasImages: values.canvasImages,
				canvasTexts: values.canvasTexts,
			};
			await api.put(`/pages/${projectId}/${id}`, {
				data: dataToSend,
				order: values.order,
			});
		} catch (e) {
			console.error("Error updating document: ", e);
		}
	};

	const updatePages = async (values) => {
		try {
			// Obtener el projectId del primer elemento
			if (values.length === 0) return;

			// Buscar projectId en diferentes lugares posibles
			let projectId = values[0].projectId;
			if (!projectId && values[0].docRef) {
				// Si docRef es un objeto con projectId
				projectId = values[0].docRef.projectId;
			}

			// Si aún no hay projectId, intentar obtenerlo del contexto o de localStorage
			if (!projectId) {
				// Intentar obtenerlo de alguna otra fuente si es necesario
				console.warn(
					"No projectId found in pages, trying to infer from context",
				);
			}

			if (!projectId) {
				console.error("No projectId found in pages");
				return;
			}

			// Preparar los datos para enviar (siempre usar estado actual, no solo value.data)
			const pagesData = values.map((value) => {
				const { docRef, id, projectId: _, updatedAt: __, ...rest } = value;
				// Fusionar value.data con el estado actual para no perder canvasImages/canvasStickers
				const dataToSend = {
					...(value.data || {}),
					templateId: value.templateId,
					images: value.images,
					texts: value.texts,
					maps: value.maps,
					transport: value.transport,
					notes: value.notes || value.data?.notes || [],
					canvasStickers: value.canvasStickers,
					canvasImages: value.canvasImages,
					canvasTexts: value.canvasTexts,
				};
				console.log("dataToSend: ", dataToSend);
				return removeUndefinedFields({
					id: value.id,
					order: value.order,
					data: dataToSend,
				});
			});

			await api.put(`/pages/${projectId}`, { pages: pagesData });
		} catch (e) {
			console.error("Error updating documents: ", e);
		}
	};

	const deletePage = async (projectId, id) => {
		try {
			await api.delete(`/pages/${projectId}/${id}`);
			console.log("Document deleted with ID: ", id);
		} catch (e) {
			console.error("Error deleting document: ", e);
		}
	};

	const getCurrentProject = async () => {
		const saved = safeStorage.getItem("current_project");
		return saved ? parseInt(saved) : 0;
	};

	const setCurrentProject = async (index) => {
		safeStorage.setItem("current_project", index.toString());
	};

	const createImage = async (image, projectId) => {
		try {
			const imageRecord = await api.post(`/images/${projectId}`, { image });
			return {
				id: imageRecord.id,
				docRef: { id: imageRecord.id },
				url: imageRecord.url,
				image: image,
			};
		} catch (e) {
			console.error("Error adding document: ", e);
			return null;
		}
	};

	const deleteImage = async (id, projectId) => {
		try {
			await api.delete(`/images/${projectId}/${id}`);
			console.log("Document deleted with ID: ", id);
		} catch (e) {
			console.error("Error deleting document: ", e);
		}
	};

	const getImages = async (projectId) => {
		try {
			let response = await api.get(`/images/${projectId}`);
			const images = Array.isArray(response)
				? response
				: (response?.data ?? response?.images ?? []);
			if (!Array.isArray(images) || images.length === 0) return [];
			return images.map((img) => ({
				id: img.id,
				url: img.url,
				docRef: { id: img.id },
			}));
		} catch (e) {
			console.error("Error reading images: ", e);
			return [];
		}
	};

	// Funciones para guías externas
	const createExternalGuide = async (name, url, description, icon) => {
		try {
			const guide = await api.post("/external-guides", {
				name,
				url,
				description,
				icon,
			});
			return guide;
		} catch (e) {
			console.error("Error creating external guide: ", e);
			return null;
		}
	};

	const readExternalGuides = async () => {
		try {
			const guides = await api.get("/external-guides");
			return guides;
		} catch (e) {
			console.error("Error reading external guides: ", e);
			return [];
		}
	};

	const updateExternalGuide = async (id, name, url, description, icon) => {
		try {
			const guide = await api.put(`/external-guides/${id}`, {
				name,
				url,
				description,
				icon,
			});
			return guide;
		} catch (e) {
			console.error("Error updating external guide: ", e);
			return null;
		}
	};

	const deleteExternalGuide = async (id) => {
		try {
			await api.delete(`/external-guides/${id}`);
		} catch (e) {
			console.error("Error deleting external guide: ", e);
		}
	};

	// Funciones para notas
	const createNote = async (externalGuideId, title, content) => {
		try {
			const note = await api.post(`/notes/${externalGuideId}`, {
				title,
				content,
			});
			return note;
		} catch (e) {
			console.error("Error creating note: ", e);
			return null;
		}
	};

	const readNotes = async (externalGuideId) => {
		try {
			const notes = await api.get(`/notes/${externalGuideId}`);
			return notes;
		} catch (e) {
			console.error("Error reading notes: ", e);
			return [];
		}
	};

	const updateNote = async (id, title, content) => {
		try {
			const note = await api.put(`/notes/${id}`, {
				title,
				content,
			});
			return note;
		} catch (e) {
			console.error("Error updating note: ", e);
			return null;
		}
	};

	const deleteNote = async (id) => {
		try {
			await api.delete(`/notes/${id}`);
		} catch (e) {
			console.error("Error deleting note: ", e);
		}
	};

	// Funciones para páginas de guías externas
	const getExternalGuidePages = async (externalGuideId) => {
		try {
			const pages = await api.get(`/external-guide-pages/${externalGuideId}`);
			return pages;
		} catch (e) {
			console.error("Error reading external guide pages: ", e);
			return [];
		}
	};

	const importExternalGuidePages = async (externalGuideId, pages) => {
		try {
			const importedPages = await api.post(
				`/external-guide-pages/${externalGuideId}/import`,
				{ pages },
			);
			return importedPages;
		} catch (e) {
			console.error("Error importing external guide pages: ", e);
			return null;
		}
	};

	// Fallback de guías cuando la API no está disponible (p. ej. usuario no logueado, backend caído)
	const FALLBACK_PREBUILT_GUIDES = [
		{
			id: "kioto",
			name: "4 días en Kioto",
			description:
				"Descubre los templos, santuarios y barrios históricos de Kioto",
			days: 4,
			tags: ["Asia", "Japón"],
			category: "guide",
			isFree: false,
			price: 4.99,
			userHasAccess: false,
		},
		{
			id: "tokio",
			name: "6 días en Tokio",
			description:
				"Explora la capital de Japón: tecnología, cultura y gastronomía",
			days: 6,
			tags: ["Asia", "Japón"],
			category: "guide",
			isFree: false,
			price: 4.99,
			userHasAccess: false,
		},
		{
			id: "ny",
			name: "7 días en Nueva York",
			description:
				"La ciudad que nunca duerme: rascacielos, cultura y vida urbana",
			days: 7,
			tags: ["América", "USA"],
			category: "guide",
			isFree: false,
			price: 9.99,
			userHasAccess: false,
		},
		{
			id: "londres",
			name: "4 días en Londres",
			description: "Museos, teatros, pubs y la realeza británica",
			days: 4,
			tags: ["Europa", "UK"],
			category: "guide",
			isFree: false,
			price: 9.99,
			userHasAccess: false,
		},
		{
			id: "seul",
			name: "5 días en Seúl",
			description:
				"Palacios, barrios tradicionales, K-pop y la modernidad coreana",
			days: 5,
			tags: ["Asia", "Corea del Sur"],
			category: "guide",
			isFree: false,
			price: 4.99,
			userHasAccess: false,
		},
		{
			id: "paris",
			name: "4 días en París",
			description: "Torre Eiffel, museos, gastronomía y la ciudad de la luz",
			days: 4,
			tags: ["Europa", "Francia"],
			category: "guide",
			isFree: false,
			price: 4.99,
			userHasAccess: false,
		},
		{
			id: "sanFrancisco",
			name: "3 días en San Francisco",
			description:
				"El puente Golden Gate, Cable Cars y los barrios con personalidad",
			days: 3,
			tags: ["USA", "California"],
			category: "guide",
			isFree: false,
			price: 4.99,
			userHasAccess: false,
		},
		// Por ahora no disponible:
		// {
		// 	id: "todo-viaje",
		// 	name: "Plantilla Todo viaje",
		// 	description: "Planificador y checklist para cualquier destino",
		// 	days: null,
		// 	tags: ["Utilidad", "Planificador"],
		// 	category: "utility",
		// 	isFree: true,
		// 	userHasAccess: false,
		// },
		{
			id: travelKitData.id,
			name: travelKitData.name,
			description: travelKitData.description,
			category: travelKitData.category,
			coverImage: travelKitData.coverImage || "/images/travelkit/cover.svg",
			tags: travelKitData.tags,
			days: travelKitData.days,
			isFree: true,
			userHasAccess: false,
		},
	];

	// Funciones para guías precargadas
	const getPrebuiltGuides = async () => {
		try {
			const guides = await api.get("/prebuilt-guides");
			// Añadir Travel Kit (plantilla local del frontend)
			const travelKit = {
				id: travelKitData.id,
				name: travelKitData.name,
				description: travelKitData.description,
				category: travelKitData.category,
				coverImage: travelKitData.coverImage || "/images/travelkit/cover.svg",
				tags: travelKitData.tags,
				days: travelKitData.days,
			};
			const fromApi = guides && guides.length > 0 ? guides : null;
			return fromApi ? [...fromApi, travelKit] : FALLBACK_PREBUILT_GUIDES;
		} catch (e) {
			console.error("Error reading prebuilt guides: ", e);
			// Si falla el backend, usar lista completa de fallback para que se vean todas las guías
			return FALLBACK_PREBUILT_GUIDES;
		}
	};

	const getPrebuiltGuidePreview = async (guideId) => {
		try {
			const result = await api.get(`/prebuilt-guides/${guideId}/preview`);
			return result;
		} catch (e) {
			console.error("Error getting prebuilt guide preview:", e);
			throw e;
		}
	};

	const importPrebuiltGuide = async (guideId) => {
		try {
			// Travel Kit: importación directa desde datos del frontend
			if (guideId === "travelkit") {
				const guide = travelKitData;
				const project = await api.post("/projects", {
					name: guide.name,
					backgroundColor: guide.backgroundColor || "#F8FAFC",
					textColor: guide.textColor || "#1E293B",
					headerBackgroundColor: guide.headerBackgroundColor || "#06B6D4",
					headerTextColor: guide.headerTextColor || "#FFFFFF",
					fontFamily: guide.fontFamily || "Poppins",
					public: false,
					prebuiltGuideId: "travelkit",
				});
				if (!project || !project.id) return null;
				const createdPages = [];
				const pages = Array.isArray(guide.pages) ? guide.pages : [];
				for (let i = 0; i < pages.length; i++) {
					const page = pages[i];
					const order = page.order !== undefined ? page.order : i;
					const pageData = {
						templateId: page.templateId,
						images: page.images || [],
						texts: page.texts || [],
						maps: page.maps || ["", "", ""],
						transport: page.transport || [],
						canvasStickers: page.canvasStickers || [],
						canvasImages: page.canvasImages || [],
						canvasTexts: page.canvasTexts || [],
					};
					// Datos específicos del Travel Kit (tripName, sections, etc.) al mismo nivel para que los templates los lean en page.data
					if (page.data && typeof page.data === "object") {
						Object.assign(pageData, page.data);
					}
					const created = await api.post(`/pages/${project.id}`, {
						order,
						data: pageData,
					});
					if (created) createdPages.push(created);
				}
				addNotification({
					id: `event-template-imported-${project.id}`,
					type: "success",
					title: "Plantilla importada",
					message: `"${guide.name}" se ha añadido a tus guías. Puedes editarla cuando quieras.`,
					action: {
						label: "Abrir guía",
						handler: "openGuide",
						projectId: project.id,
					},
				});
				return { project, pages: createdPages };
			}
			// NY, Kioto, Tokio, etc.: importación vía backend (datos en guia-backend/data/prebuiltGuides/*.js)
			const result = await api.post(`/prebuilt-guides/${guideId}/import`);
			const proj = result?.project || result;
			if (proj?.id) {
				addNotification({
					id: `event-template-imported-${proj.id}`,
					type: "success",
					title: "Plantilla importada",
					message: `"${
						proj.name || "Guía"
					}" se ha añadido a tus guías. Puedes editarla cuando quieras.`,
					action: {
						label: "Abrir guía",
						handler: "openGuide",
						projectId: proj.id,
					},
				});
			}
			return result;
		} catch (e) {
			console.error("Error importing prebuilt guide: ", e);
			// Propagar el error con mensaje descriptivo
			const error = new Error(e.message || "Error al importar la guía");
			error.status = e.status;
			// Añadir contexto según el código de error
			if (e.status === 403) {
				if (
					e.message?.includes("GUIDE_LIMIT") ||
					e.message?.includes("límite")
				) {
					error.code = "GUIDE_LIMIT";
					error.message =
						"Has alcanzado el límite de guías de tu plan. Hazte Pro para crear más.";
				} else if (
					e.message?.includes("acceso") ||
					e.message?.includes("ACCESS")
				) {
					error.code = "ACCESS_DENIED";
					error.message = "No tienes acceso a esta guía";
				}
			}
			throw error;
		}
	};

	// Funciones para guías compartidas
	const createSharedGuide = async (projectId, isPublic = false) => {
		try {
			const result = await api.post("/shared-guides", {
				projectId,
				isPublic,
			});
			return result;
		} catch (e) {
			console.error("Error creating shared guide: ", e);
			if (e?.response?.data?.error) {
				return {
					error: e.response.data.error,
					message: e.response.data.message,
				};
			}
			return null;
		}
	};

	const getSharedGuide = async (token) => {
		const result = await api.get(`/shared-guides/${token}`);
		return result;
	};

	const getSharedGuides = async () => {
		try {
			const guides = await api.get("/shared-guides");
			return guides;
		} catch (e) {
			console.error("Error getting shared guides: ", e);
			return [];
		}
	};

	const deleteSharedGuide = async (id) => {
		try {
			await api.delete(`/shared-guides/${id}`);
			return true;
		} catch (e) {
			console.error("Error deleting shared guide: ", e);
			return false;
		}
	};

	const updateSharedGuidePage = async (token, pageId, data) => {
		try {
			await api.patch(`/shared-guides/${token}/pages/${pageId}`, { data });
			return true;
		} catch (e) {
			console.error("Error updating shared guide page: ", e);
			return false;
		}
	};

	// ========== FUNCIONES DE SUSCRIPCIÓN Y PAGOS ==========

	// Funciones para favoritos
	const getFavorites = async () => {
		try {
			const result = await api.get("/user/favorites");
			const ids =
				result.guideIds || result.favorites?.map((f) => f.guideId) || [];
			setFavorites(ids);
			return ids;
		} catch (e) {
			console.error("Error leyendo favoritos:", e);
			return [];
		}
	};

	const addFavorite = async (guideId) => {
		try {
			await api.post("/user/favorites", { guideId });
			setFavorites((prev) =>
				prev.includes(guideId) ? prev : [...prev, guideId],
			);
			analytics.trackEvent("add_to_favorites", { guide_id: String(guideId) });
			addNotification({
				id: `event-favorite-added-${guideId}`,
				type: "success",
				title: "Favorito añadido",
				message:
					"La guía se ha guardado en tus favoritos. Accede a ella desde el menú.",
				action: { label: "Ver favoritos", path: "/favoritos" },
			});
		} catch (e) {
			console.error("Error añadiendo favorito:", e);
			throw e;
		}
	};

	const removeFavorite = async (guideId) => {
		try {
			await api.delete(`/user/favorites/${encodeURIComponent(guideId)}`);
			setFavorites((prev) => prev.filter((id) => id !== guideId));
			analytics.trackEvent("remove_from_favorites", {
				guide_id: String(guideId),
			});
		} catch (e) {
			console.error("Error eliminando favorito:", e);
			throw e;
		}
	};

	const toggleFavorite = async (guideId) => {
		const isFav = favorites.includes(guideId);
		if (isFav) {
			await removeFavorite(guideId);
		} else {
			await addFavorite(guideId);
		}
	};

	/**
	 * Refresca la información de suscripción y límites del usuario
	 */
	const refreshSubscription = async () => {
		try {
			const response = await api.get("/auth/me");
			if (response.user?.emailVerified !== undefined) {
				setEmailVerified(response.user.emailVerified);
			}
			if (response.subscription) {
				setSubscription(response.subscription);
			}
			if (response.limits) {
				setLimits(response.limits);
			}
			if (response.purchasedGuides) {
				setPurchasedGuides(response.purchasedGuides);
			}
			if (response.favorites) {
				setFavorites(response.favorites);
			}
			return response;
		} catch (e) {
			console.error("Error refreshing subscription:", e);
			return null;
		}
	};

	/**
	 * Obtiene el estado de la suscripción
	 */
	const getSubscriptionStatus = async () => {
		try {
			const status = await api.get("/stripe/subscription-status");
			return status;
		} catch (e) {
			console.error("Error getting subscription status:", e);
			return null;
		}
	};

	/**
	 * Crea una sesión de checkout en Stripe
	 * @param {string} priceType - 'COMPLETE' para suscripción o 'GUIDE' para compra individual
	 * @param {string} guideId - ID de la guía (solo para priceType='GUIDE')
	 */
	const createCheckoutSession = async (priceType, guideId = null) => {
		try {
			const body = { priceType };
			if (guideId) body.guideId = guideId;
			const result = await api.post("/stripe/create-checkout-session", body);
			return result;
		} catch (e) {
			console.error("Error creating checkout session:", e);
			throw e;
		}
	};

	/**
	 * Abre el portal de cliente de Stripe para gestionar suscripción
	 */
	const openCustomerPortal = async () => {
		try {
			const result = await api.post("/stripe/create-portal-session", {});
			if (result.url) {
				window.location.href = result.url;
			}
			return result;
		} catch (e) {
			console.error("Error opening customer portal:", e);
			throw e;
		}
	};

	/**
	 * Verifica si el usuario tiene acceso a una guía específica
	 */
	const checkGuideAccess = (guideId, guideInfo = null) => {
		// Si la guía es gratis
		if (guideInfo?.isFree) {
			return {
				hasAccess: !!user,
				hasFullAccess: !!user,
				reason: user ? "FREE_GUIDE" : "REQUIRES_AUTH",
			};
		}

		// Si tiene suscripción Pro (COMPLETE)
		if (subscription?.isComplete) {
			return {
				hasAccess: true,
				hasFullAccess: true,
				reason: "SUBSCRIPTION",
			};
		}

		// Si ha comprado la guía
		if (purchasedGuides.includes(guideId)) {
			return {
				hasAccess: true,
				hasFullAccess: true,
				reason: "PURCHASED",
			};
		}

		// Sin acceso
		return {
			hasAccess: false,
			hasFullAccess: false,
			reason: user ? "REQUIRES_PURCHASE" : "REQUIRES_AUTH",
		};
	};

	/**
	 * Verifica si el usuario puede crear una nueva guía
	 */
	const canCreateGuide = () => {
		if (!limits) return { allowed: true }; // Si no hay info de límites, permitir
		if (limits.maxGuides === -1) return { allowed: true }; // -1 = ilimitado
		return {
			allowed: limits.canCreateGuide,
			currentGuides: limits.currentGuides,
			maxGuides: limits.maxGuides,
			reason: limits.canCreateGuide ? null : "GUIDE_LIMIT_REACHED",
		};
	};

	/**
	 * Verifica si el usuario puede exportar
	 */
	const canExport = () => {
		if (!limits) return false;
		return limits.canExport;
	};

	/**
	 * Inicia el proceso de compra de suscripción
	 */
	const purchaseSubscription = async () => {
		try {
			analytics.trackEvent("begin_checkout", { type: "subscription" });
			const result = await createCheckoutSession("COMPLETE");
			if (result.url) {
				window.location.href = result.url;
			}
			return result;
		} catch (e) {
			console.error("Error purchasing subscription:", e);
			throw e;
		}
	};

	/**
	 * Inicia el proceso de compra de una guía individual
	 */
	const purchaseGuide = async (guideId) => {
		try {
			analytics.trackEvent("begin_checkout", {
				type: "guide",
				guide_id: String(guideId),
			});
			const result = await createCheckoutSession("GUIDE", guideId);
			if (result.url) {
				window.location.href = result.url;
			}
			return result;
		} catch (e) {
			console.error("Error purchasing guide:", e);
			throw e;
		}
	};

	// --------------------------------------------------------------------------
	// Packs (bundles: guía + stickers + páginas extra)
	// --------------------------------------------------------------------------

	/**
	 * Lista los packs disponibles con sus flags de acceso para el user
	 * autenticado (isOwned, isUnlockedByPro, hasAccess).
	 * Referencia estable: evita que PacksSection vuelva a fetch en cada render.
	 */
	const getPacks = useCallback(async () => {
		try {
			const res = await api.get("/packs");
			return res?.packs || [];
		} catch (e) {
			console.error("Error loading packs:", e);
			return [];
		}
	}, []);

	/**
	 * Recarga las entitlements del usuario (tras una compra, etc.).
	 */
	const refreshEntitlements = async () => {
		try {
			const res = await api.get("/packs/me/entitlements");
			if (res?.entitlements) {
				setEntitlements(res.entitlements);
				return res.entitlements;
			}
			return null;
		} catch (e) {
			console.error("Error refreshing entitlements:", e);
			return null;
		}
	};

	/**
	 * Inicia el proceso de compra de un pack (Stripe Checkout).
	 */
	const purchasePack = async (packId) => {
		try {
			analytics.trackEvent("begin_checkout", {
				type: "pack",
				pack_id: String(packId),
			});
			const result = await api.post("/stripe/create-checkout-session", {
				priceType: "PACK",
				packId,
			});
			if (result?.url) {
				window.location.href = result.url;
			}
			return result;
		} catch (e) {
			console.error("Error purchasing pack:", e);
			throw e;
		}
	};

	/**
	 * Helpers derivados de entitlements para usar desde componentes.
	 * Encapsulan la lógica de "tiene acceso a X" para que los componentes
	 * no tengan que conocer la forma del objeto.
	 */
	const hasPackEntitlement = (packId) => {
		if (!entitlements) return false;
		if (entitlements.proUnlocks?.includes(packId)) return true;
		return !!entitlements.ownedPackIds?.includes(packId);
	};
	const hasCustomStickersEntitlement = (countryId) => {
		if (!entitlements) return false;
		return !!entitlements.customStickerCountries?.includes(countryId);
	};
	const getExtraPagesForGuide = (guideId) => {
		if (!entitlements?.extraPagesPerGuide) return 0;
		return entitlements.extraPagesPerGuide[guideId] || 0;
	};

	/**
	 * Envía una propuesta de guía al backend (funciona con o sin sesión)
	 * @param {string} text - Texto de la propuesta (ej: "5 días en Roma")
	 * @param {string} [searchQuery] - Búsqueda que no encontró resultados
	 */
	const submitGuideProposal = async (text, searchQuery) => {
		try {
			const response = await api.post("/guide-proposals", {
				text: (text || "").trim(),
				searchQuery: searchQuery ? String(searchQuery).trim() : null,
			});
			return response;
		} catch (e) {
			console.error("Error enviando propuesta:", e);
			throw e;
		}
	};

	// ========== VERIFICACIÓN DE EMAIL ==========

	const verifyEmail = async (token) => {
		try {
			const result = await api.post("/auth/verify-email", { token });
			if (result.emailVerified) {
				setEmailVerified(true);
				setUser((prev) => (prev ? { ...prev, emailVerified: true } : null));
			}
			return result;
		} catch (e) {
			console.error("Error verificando email:", e);
			throw e;
		}
	};

	const resendVerificationEmail = async () => {
		try {
			const result = await api.post("/auth/resend-verification", {});
			return result;
		} catch (e) {
			console.error("Error reenviando email de verificación:", e);
			throw e;
		}
	};

	const forgotPassword = async (email) => {
		const result = await api.post("/auth/forgot-password", { email });
		return result;
	};

	const resetPassword = async (token, password) => {
		const result = await api.post("/auth/reset-password", { token, password });
		return result;
	};

	const changePassword = async (currentPassword, newPassword) => {
		const result = await api.post("/auth/change-password", {
			currentPassword,
			newPassword,
		});
		return result;
	};

	const deleteAccount = async () => {
		await api.delete("/auth/delete-account");
	};

	/**
	 * Comprueba si una acción requiere email verificado y el usuario no lo tiene.
	 * @param {'payment'|'share_public'|'create_guide'|'use_premium_guide'} action
	 * @returns {boolean} true si la acción está bloqueada
	 */
	const isBlockedByEmailVerification = (action) => {
		if (!user || emailVerified) return false;
		const blockedActions = [
			"payment",
			"share_public",
			"create_guide",
			"use_premium_guide",
		];
		return blockedActions.includes(action);
	};

	// ========== STICKER COLLECTIONS ==========
	const getStickerCollections = async () => {
		try {
			return await api.get("/stickers/collections");
		} catch (e) {
			console.error("Error obteniendo colecciones de stickers:", e);
			return [];
		}
	};

	const getStickerCollection = async (id) => {
		try {
			return await api.get(`/stickers/collections/${encodeURIComponent(id)}`);
		} catch (e) {
			console.error("Error obteniendo colección de stickers:", e);
			return null;
		}
	};

	const getStickerAssetUrl = (assetId) => {
		if (!assetId) return "";
		const token = safeStorage.getItem("token");
		const base = `${api.baseURL}/stickers/assets/${encodeURIComponent(
			assetId,
		)}`;
		return token ? `${base}?token=${encodeURIComponent(token)}` : base;
	};

	const getCustomStickerAssetUrl = (countryId, filename) => {
		if (!countryId || !filename) return "";
		const token = safeStorage.getItem("token");
		const base = `${api.baseURL}/custom-stickers/${encodeURIComponent(
			countryId,
		)}/assets/${encodeURIComponent(filename)}`;
		return token ? `${base}?token=${encodeURIComponent(token)}` : base;
	};

	const getAuthenticatedUrl = (url) => {
		if (!url) return "";
		const token = safeStorage.getItem("token");
		if (!token) return url;
		try {
			const u = new URL(url, window.location.origin);
			u.searchParams.set("token", token);
			return u.toString();
		} catch {
			return url;
		}
	};

	const getStickerPublicUrl = (collectionId, filename) => {
		return `${api.baseURL}/stickers/public/${encodeURIComponent(
			collectionId,
		)}/${filename}`;
	};

	// ========== PASSPORT ==========
	const getPassport = async () => {
		try {
			return await api.get("/passport");
		} catch (e) {
			console.error("Error obteniendo pasaporte:", e);
			throw e;
		}
	};

	const updatePassport = async (data) => {
		try {
			return await api.put("/passport", data);
		} catch (e) {
			console.error("Error actualizando pasaporte:", e);
			throw e;
		}
	};

	const addPassportCountry = async (countryData) => {
		try {
			return await api.post("/passport/countries", countryData);
		} catch (e) {
			console.error("Error añadiendo país:", e);
			throw e;
		}
	};

	const updatePassportCountry = async (countryId, data) => {
		try {
			return await api.put(`/passport/countries/${countryId}`, data);
		} catch (e) {
			console.error("Error actualizando país:", e);
			throw e;
		}
	};

	const deletePassportCountry = async (countryId) => {
		try {
			return await api.delete(`/passport/countries/${countryId}`);
		} catch (e) {
			console.error("Error eliminando país:", e);
			throw e;
		}
	};

	const addPassportPhoto = async (countryId, photoData) => {
		try {
			return await api.post(
				`/passport/countries/${countryId}/photos`,
				photoData,
			);
		} catch (e) {
			console.error("Error añadiendo foto:", e);
			throw e;
		}
	};

	const deletePassportPhoto = async (countryId, photoId) => {
		try {
			return await api.delete(
				`/passport/countries/${countryId}/photos/${photoId}`,
			);
		} catch (e) {
			console.error("Error eliminando foto:", e);
			throw e;
		}
	};

	// ========== PASSPORT SHARE ==========
	const sharePassport = async (isPublic = false) => {
		try {
			// Enviar el origen actual para que el enlace apunte a esta app (guia), no a otra (ej. guia-bali)
			const origin =
				typeof window !== "undefined" ? window.location.origin : "";
			return await api.post("/passport/share", { isPublic, origin });
		} catch (e) {
			console.error("Error compartiendo pasaporte:", e);
			throw e;
		}
	};

	const getSharedPassport = async (token) => {
		try {
			return await api.get(`/passport/shared/${token}`);
		} catch (e) {
			console.error("Error obteniendo pasaporte compartido:", e);
			throw e;
		}
	};

	// ========== TARJETA VIAJERA ==========
	const getTravelCard = async () => {
		try {
			const data = await api.get("/travel-card");
			return data;
		} catch (e) {
			console.error("Error obteniendo tarjeta viajera:", e);
			throw e;
		}
	};

	const saveTravelCard = async (data) => {
		try {
			return await api.put("/travel-card", data);
		} catch (e) {
			console.error("Error guardando tarjeta viajera:", e);
			throw e;
		}
	};

	const contextValue = {
		user,
		initialLoading,
		// Verificación de email
		emailVerified,
		verifyEmail,
		resendVerificationEmail,
		forgotPassword,
		resetPassword,
		changePassword,
		isBlockedByEmailVerification,
		// Suscripción y límites
		subscription,
		limits,
		purchasedGuides,
		favorites,
		// Favoritos
		getFavorites,
		addFavorite,
		removeFavorite,
		toggleFavorite,
		// Auth
		handleLogout,
		updateProfile,
		createUser,
		deleteAccount,
		// Projects
		createProject,
		readProjects,
		readProjectsPaginated,
		readPublicProjects,
		readProject,
		updateProject,
		deleteProject,
		// Pages
		createPage,
		readPages,
		updatePage,
		updatePages,
		deletePage,
		// Images
		getCurrentProject,
		setCurrentProject,
		createImage,
		deleteImage,
		getImages,
		// External guides
		createExternalGuide,
		readExternalGuides,
		updateExternalGuide,
		deleteExternalGuide,
		// Notes
		createNote,
		readNotes,
		updateNote,
		deleteNote,
		// External guide pages
		getExternalGuidePages,
		importExternalGuidePages,
		// Prebuilt guides
		getPrebuiltGuides,
		getPrebuiltGuidePreview,
		importPrebuiltGuide,
		// Shared guides
		createSharedGuide,
		getSharedGuide,
		getSharedGuides,
		deleteSharedGuide,
		updateSharedGuidePage,
		// Suscripción y pagos
		refreshSubscription,
		getSubscriptionStatus,
		createCheckoutSession,
		openCustomerPortal,
		checkGuideAccess,
		canCreateGuide,
		canExport,
		purchaseSubscription,
		purchaseGuide,
		// Packs
		entitlements,
		getPacks,
		refreshEntitlements,
		purchasePack,
		hasPackEntitlement,
		hasCustomStickersEntitlement,
		getExtraPagesForGuide,
		submitGuideProposal,
		// Sticker collections
		getStickerCollections,
		getStickerCollection,
		getStickerAssetUrl,
		getCustomStickerAssetUrl,
		getAuthenticatedUrl,
		getStickerPublicUrl,
		// Passport
		getPassport,
		updatePassport: updatePassport,
		addPassportCountry,
		updatePassportCountry,
		deletePassportCountry,
		addPassportPhoto,
		deletePassportPhoto,
		sharePassport,
		getSharedPassport,
		// Tarjeta viajera
		getTravelCard,
		saveTravelCard,
		// Notificaciones
		notifications,
		unreadNotificationCount,
		dismissNotification,
		dismissAllNotifications,
	};

	return (
		<ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>
	);
};

export const useFirebase = () => {
	const context = useContext(ApiContext);

	if (!context) {
		throw new Error(
			"useFirebase debe ser utilizado dentro de un ApiProvider o FirebaseProvider",
		);
	}

	return context;
};
